import type { ReactNode } from 'react';

// ─── Tipos ───────────────────────────────────────────────────────────────────

/** Props do componente FormField */
export interface FormFieldProps {
  /** Label do campo — exibido acima do input */
  readonly label: string;
  /** ID do campo — vincula o label ao input via htmlFor */
  readonly htmlFor?: string;
  /** Mensagem de erro — exibida abaixo do campo em vermelho */
  readonly error?: string;
  /** Texto de ajuda — exibido abaixo do campo em cinza */
  readonly hint?: string;
  /** Indica que o campo é obrigatório */
  readonly required?: boolean;
  /** Input ou outro controle de formulário */
  readonly children: ReactNode;
  /** Classes Tailwind adicionais para o wrapper */
  readonly className?: string;
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Molecule FormField — combina Label + Input + mensagem de erro/hint.
 *
 * Usada em todos os formulários do projeto para manter consistência visual.
 * Recebe dados via props — sem estado interno ou chamadas à API.
 *
 * @example
 * ```tsx
 * <FormField
 *   label="Nome completo"
 *   error={form.formState.errors.name?.message}
 *   required
 * >
 *   <Input {...form.register('name')} aria-invalid={!!form.formState.errors.name} />
 * </FormField>
 * ```
 */
export function FormField({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className,
}: FormFieldProps) {
  const errorId = htmlFor ? `${htmlFor}-error` : undefined;
  const hintId = htmlFor ? `${htmlFor}-hint` : undefined;

  return (
    <div className={`flex flex-col gap-quark ${className ?? ''}`}>
      {/* Label */}
      <label htmlFor={htmlFor} className="ts-heading-xxs !font-medium text-ctx-content-title">
        {label}
        {required && (
          <span className="ml-quark text-ctx-feedback-error" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {/* Input slot */}
      {children}

      {/* Hint (quando não há erro) */}
      {hint && !error && (
        <p id={hintId} className="ts-caption-xxs text-ctx-content-placeholder">
          {hint}
        </p>
      )}

      {/* Mensagem de erro */}
      {error && (
        <p id={errorId} className="ts-caption-xxs text-ctx-feedback-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
